<template>
  <div @click.capture="interceptMetricLink">
    <!-- Section header -->
    <div class="mb-6">
      <div class="ext-section-number">Performance</div>
      <h1 class="ext-section-title" v-if="selectedMetric">{{ selectedMetric.metric_name }}</h1>
      <h1 class="ext-section-title" v-else-if="selectedDomain">{{ selectedDomain.name }}</h1>
      <h1 class="ext-section-title" v-else>Performance Metrics</h1>
      <p class="ext-section-desc" v-if="selectedMetric">
        Trend and patient-level detail for this metric. Select a domain from the sidebar to see all metrics in that group.
      </p>
      <p class="ext-section-desc" v-else-if="selectedDomain && selectedDomain.description">
        {{ selectedDomain.description }}
      </p>
      <p class="ext-section-desc" v-else>
        Composable metrics tracking clinical and operational performance across value-based care contracts. Select a domain from the sidebar to drill into individual measures, view longitudinal trends, and access patient-level worklists.
      </p>
    </div>

    <div v-if="loading" style="padding: 40px; text-align: center; color: #A9A29A; font-size: 13px;">Loading performance domains…</div>

    <div v-else-if="!visibleDomains.length" style="padding: 40px; text-align: center; color: #A9A29A; font-size: 13px;">No performance domains configured.</div>

    <!-- Metric detail -->
    <div v-else-if="selectedMetric" class="space-y-6">
      <div class="flex items-center justify-between mb-2">
        <div class="ext-section-meta">{{ selectedDomain?.name }}</div>
        <YearSelector v-if="availableYears.length" v-model="year" :years="availableYears" />
      </div>

      <PerformanceTrend
        v-if="year"
        :metric-ids="[selectedMetric.metric_id]"
        :year="year"
        :entity-id="entityIdNumber"
      />
    </div>

    <!-- Domain view -->
    <div v-else-if="selectedDomain" class="space-y-4">
      <div class="flex items-center justify-end mb-2">
        <YearSelector v-if="availableYears.length" v-model="year" :years="availableYears" />
      </div>
      <ComposableMetricScorecard
        v-if="year"
        :entity="entityProp"
        :entity-type-name="entityTypeName"
        :metric-ids="selectedDomain.publicMetricIds"
        :observation-year="year"
        :auto-height="true"
      />
    </div>
  </div>
</template>

<script>

import ComposableMetricScorecard from '@/components/InsightsNew/ComposableMetricScorecard.vue';
import PerformanceTrend from '@/components/InsightsNew/PerformanceTrend.vue';
import YearSelector from './YearSelector.vue';

export default {
  name: 'ExternalPerformance',
  components: { ComposableMetricScorecard, PerformanceTrend, YearSelector },
  props: {
    entityid: { type: [String, Number], default: null },
    selectedDomainId: { type: Number, default: null },
    selectedMetricId: { type: Number, default: null },
  },
  emits: ['domains-loaded', 'select-metric'],
  data() {
    return {
      loading: true,
      domains: [],
      entityTypeName: 'Practice',
      year: null,
      availableYears: [],
    };
  },
  computed: {
    entityProp() {
      return this.entityid ? { id: Number(this.entityid) } : {};
    },
    entityIdNumber() {
      return this.entityid ? Number(this.entityid) : null;
    },
    visibleDomains() {
      return this.domains.filter(d => d.publicMetricIds.length > 0);
    },
    selectedDomain() {
      if (!this.selectedDomainId) return null;
      return this.visibleDomains.find(d => d.id === this.selectedDomainId) || null;
    },
    selectedMetric() {
      if (!this.selectedDomain || !this.selectedMetricId) return null;
      return (this.selectedDomain.metrics || []).find(
        m => m.metric_id === this.selectedMetricId
      ) || null;
    },
  },
  watch: {
    entityid: {
      immediate: true,
      handler() { this.fetchDomains(); },
    },
    selectedDomainId() { this.applyDefaultYearForSelectedDomain(); },
    selectedDomain() { this.applyDefaultYearForSelectedDomain(); },
  },
  mounted() { this.fetchAvailableYears(); },
  methods: {
    interceptMetricLink(e) {
      const link = e.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      const match = href && href.match(/\/insight\/metric\/(\d+)/);
      if (match) {
        e.preventDefault();
        e.stopPropagation();
        const metricId = Number(match[1]);
        // Find which domain owns this metric
        for (const d of this.visibleDomains) {
          const m = (d.metrics || []).find(m => m.metric_id === metricId);
          if (m) {
            this.$emit('select-metric', { domainId: d.id, metricId });
            return;
          }
        }
      }
    },
    applyDefaultYearForSelectedDomain() {
      if (!this.selectedDomain) return;
      const domainName = (this.selectedDomain.name || '').toLowerCase();
      if (domainName.includes('quality') || domainName.includes('panel management')) {
        this.year = 2026;
      }
    },
    async fetchAvailableYears() {
      try {
        const { data } = await this.$axios.post('/api/impact_analytics/observation-dates/', {});
        const years = Object.keys(data.dates_by_year || {}).map(Number).sort((a, b) => b - a);
        this.availableYears = years;
        if (!this.year && years.length) this.year = years[0];
      } catch { this.year = new Date().getFullYear(); }
    },
    async fetchDomains() {
      this.loading = true;
      try {
        const { data } = await this.$axios.get('/api/performancedomains/');
        this.domains = (data || []).map(d => {
          const metrics = (d.composable_metrics || []).filter(m => m.is_public !== false && m.is_composable);
          return { id: d.id, name: d.name, description: d.description, metrics, publicMetricIds: metrics.map(m => m.metric_id) };
        });
        this.$emit('domains-loaded', this.visibleDomains);
        this.applyDefaultYearForSelectedDomain();
      } catch { this.domains = []; this.$emit('domains-loaded', []); }
      finally { this.loading = false; }
    },
  },
};
</script>
